import { LegendItem } from '@/lib/Charts/common';
import { PerformanceAudienceTrendPoint } from '@/services/endpoints/performanceAudience';
import colors from '@/tailwind/colors';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usePerformanceAudienceFormatters } from '../../hooks/usePerformanceAudienceFormatters';
import { PLATFORM_CHART_COLORS } from '../../constants';

type PlatformTrendChartProps = {
  data: PerformanceAudienceTrendPoint[];
  isPercentage?: boolean;
};

const PlatformTrendTooltip: FC<{
  active?: boolean;
  payload?: { payload: PerformanceAudienceTrendPoint }[];
  isPercentage?: boolean;
}> = ({ active, payload, isPercentage }) => {
  const { t, i18n } = useTranslation('performanceAudience');
  const { formatNumber } = usePerformanceAudienceFormatters();

  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0].payload;
  const formatValue = (value: number) => {
    if (!isPercentage) {
      return formatNumber(value);
    }

    const formatted = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
    return `${formatted}%`;
  };

  return (
    <div className='rounded-lg border border-primary-border bg-white p-3 text-xs shadow-md'>
      <p className='mb-2 font-semibold text-primary-900'>{point.label}</p>
      <p className='text-brand-700'>
        {t('platforms.google')}: {formatValue(point.google)}
      </p>
      <p className='text-primary-600'>
        {t('platforms.meta')}: {formatValue(point.meta)}
      </p>
    </div>
  );
};

export const PlatformTrendChart: FC<PlatformTrendChartProps> = ({ data, isPercentage }) => {
  const { t } = useTranslation('performanceAudience');

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap justify-end gap-6'>
        <LegendItem color={PLATFORM_CHART_COLORS.google} name={t('platforms.googleAds')} />
        <LegendItem color={PLATFORM_CHART_COLORS.meta} name={t('platforms.meta')} />
      </div>
      <ResponsiveContainer width='100%' height={280}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id='googleAreaGradient' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={PLATFORM_CHART_COLORS.google} stopOpacity={0.2} />
              <stop offset='100%' stopColor={PLATFORM_CHART_COLORS.google} stopOpacity={0} />
            </linearGradient>
            <linearGradient id='metaAreaGradient' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={PLATFORM_CHART_COLORS.meta} stopOpacity={0.15} />
              <stop offset='100%' stopColor={PLATFORM_CHART_COLORS.meta} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray='none' stroke='var(--gray-300)' horizontal vertical={false} />
          <XAxis
            dataKey='label'
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: '0.75rem', fill: colors.tertiary[600] }}
            dy={8}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: '0.75rem', fill: colors.tertiary[600] }}
            tickFormatter={(value) => (isPercentage ? `${value}%` : value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
            width={48}
          />
          <Tooltip content={<PlatformTrendTooltip isPercentage={isPercentage} />} />
          <Area type='monotone' dataKey='google' fill='url(#googleAreaGradient)' stroke='none' />
          <Area type='monotone' dataKey='meta' fill='url(#metaAreaGradient)' stroke='none' />
          <Line
            type='monotone'
            dataKey='google'
            stroke={PLATFORM_CHART_COLORS.google}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, fill: PLATFORM_CHART_COLORS.google, stroke: '#fff', strokeWidth: 2 }}
          />
          <Line
            type='monotone'
            dataKey='meta'
            stroke={PLATFORM_CHART_COLORS.meta}
            strokeWidth={2.5}
            strokeDasharray='6 4'
            dot={false}
            activeDot={{ r: 4, fill: PLATFORM_CHART_COLORS.meta, stroke: '#fff', strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
